import { css } from '@emotion/react';
import { useContext, useState } from 'react';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm, Resolver, SubmitHandler } from 'react-hook-form';
import t from 'react-translate';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { MentoringProgramRole } from 'redux/schemas/models/mentoring-program-enrollments';
import { SaveStatus } from 'redux/schemas/app/mentoring-programs';
import { setSaveStatus } from 'redux/actions/mentoring-programs';
import { createMentoringProgramRecruitment, deleteMentoringProgramRecruitment } from 'redux/actions/mentoring-program-recruitments';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { overlayStyles } from 'athena/components/mentoring-program/program-configuration/program-setup/styles';
import NvDatePicker, { DatePickerType } from 'shared/components/inputs/nv-datepicker';
import NvPopover from 'shared/components/nv-popover';
import NvConfirmationPopover from 'shared/components/nv-confirmation-popover';
import NvSwitch from 'shared/components/inputs/nv-switch';
import { NvTooltip } from 'shared/components/nv-tooltip';
import RegistrationPage, { whatToExpectContentDefaultValue, whoThisIsForContentDefaultValue, whyJoinContentDefaultValue } from './registration-page';
import { config } from '@config/pendo.config.json';


const datePickerStyles = css`
  .react-datepicker-wrapper {
    width: 100%;
  }
`;

const RecruitmentForm = () => {
  const dispatch = useAppDispatch();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const { id: programId,
    mentorRecruitmentEnabled,
    recruitments,
   } = mentoringProgram || {};

  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram?.id));

  const validationSchema = yup.object().shape({
    whoThisIsForContent: yup.string(),
    whatToExpectContent: yup.string(),
    whyJoinContent: yup.string(),
    includeWhoThisIsFor: yup.boolean(),
    includeWhatToExpect: yup.boolean(),
    includeWhyJoin: yup.boolean(),
  });

  const defaultValues = {
    whoThisIsForContent: whoThisIsForContentDefaultValue,
    whatToExpectContent: whatToExpectContentDefaultValue,
    whyJoinContent: whyJoinContentDefaultValue,
    includeWhoThisIsFor: true,
    includeWhatToExpect: true,
    includeWhyJoin: true,
  };

  const rhfMethods = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  const label = mentorRecruitmentEnabled
    ? t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ARIA_LABEL.TURN_REGISTRATION_PAGE_OFF()
    : t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.ARIA_LABEL.TURN_REGISTRATION_PAGE_ON();

  const handleConfirm = () => {
    // Disable Recruitment
    dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
    wrapThunkAction(
      dispatch(
        deleteMentoringProgramRecruitment({
          programId,
          recruitment: recruitments?.mentor,
        }),
      ),
    ).then(() => {
      dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
    }).finally(() => {
      setTimeout(() => {
        dispatch(setSaveStatus({ newStatus: null }));
      }, 3000);
      setShowConfirmation(false);
    });
  };

  const handleChange = () => {
    if (mentorRecruitmentEnabled) {
      setShowConfirmation(true);
    } else {
      // Enable Recruitment
      dispatch(setSaveStatus({ newStatus: SaveStatus.IN_PROGRESS }));
      wrapThunkAction(
        dispatch(
          createMentoringProgramRecruitment({
            programId,
          }),
        ),
      ).then(() => {
        dispatch(setSaveStatus({ newStatus: SaveStatus.COMPLETED }));
      }).finally(() => {
        setTimeout(() => {
          dispatch(setSaveStatus({ newStatus: null }));
        }, 3000);
        setShowConfirmation(false);
      });
    }
  };

  return (
    <div>
      <FormProvider {...rhfMethods}>
        <form>
          <div className='d-flex mb-2 align-items-center'>
            <div className='heading-5 mr-2'>{t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.TITLE()}</div>
            <NvPopover
              enabled
              show={showConfirmation}
              overlayStyles={overlayStyles}
              content={(
                <NvConfirmationPopover
                  onCancel={() => setShowConfirmation(false)}
                  onConfirm={handleConfirm}
                  header={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.WARNING.TITLE()}
                  message={t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.WARNING.CONTENT(mentoringProgramAliases)}
                  cancelBtnText={t.FORM.CANCEL()}
                  confirmBtnText={t.FORM.OK()}
                />
              )}
              rootClose
              onHide={() => setShowConfirmation(false)}
              className='d-inline-flex mw-100'
              preventOverflow
            >
              <NvTooltip preventOverflow={false} text={label}>
                <NvSwitch
                  checked={mentorRecruitmentEnabled}
                  onChange={handleChange}
                  enabledLabel={t.SHARED.ON().toUpperCase()}
                  disabledLabel={t.SHARED.OFF().toUpperCase()}
                  // dataQa={config.pendo.athena.mentorshipProgram.settings.feedback.enabledSwitch}
                  aria-label={label}
                />
              </NvTooltip>
            </NvPopover>
          </div>
          <div>{t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.REGISTRATION_PAGE.DESCRIPTION(mentoringProgramAliases)}</div>
          {/* maybe make this a separate component */}
          <div className='d-flex pt-4 pb-6'>
            <div className='w-50'>
              <div className='d-flex w-100 semi-bold gray-2 pb-1'>
                {t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.RECRUITMENT_INTEREST_DEADLINE()}
              </div>
              <div className='d-flex w-100'>{t.MENTORING_PROGRAMS.SETTINGS.TABS.RECRUITMENT.RECRUITMENT_INTEREST_DEADLINE_DESCRIPTION()}</div>
            </div>
            <div className='d-flex w-50 align-items-center pl-4'>
              <NvDatePicker
                className='w-100'
                css={datePickerStyles}
                type={DatePickerType.DATETIME}
                onChange={() => {}}
                placement='left'
              />
            </div>
          </div>
          {/* end of maybe make this a separate component */}
          <RegistrationPage role={MentoringProgramRole.MENTORSHIP_PROGRAM_MENTOR} />
        </form>
      </FormProvider>
    </div>
  );
};

export default RecruitmentForm;
